import React, { type FC } from 'react'
import { AppBar, Box, Link, Stack, type SvgIconProps, type SxProps, type Theme, Toolbar } from '@mui/material'

import { Translation } from '../../../domain'

import { DocumentationType, LogoutReason } from '../../../application'

import { CybusMinimalLogo, DocumentationFilledIcon, LogoutIcon } from '../common'
import { DocumentationLink } from '../Documentation'
import { useTranslator } from '../Internationalization'
import { AbsoluteRouteOnlyPath, useRouting } from '../routing'
import { useAppUsecase } from '../State'
import { useNavigationFormatter } from './Hooks'
import { NavigationPermissionedLink } from './Link'
import { SessionCountdown } from './SessionCountdown'

const TOPBAR_HEIGHT = 52

/**
 * There are 2 types of links:
 * - text links (nav items with icon and text, on the left)
 * - icon links (nav items with icon only, on the right)
 */

// left nav items styles (text links)
const linkTextLinkStyle: SxProps<Theme> = (theme) => ({
    color: 'grey.700',
    borderBottom: 2,
    borderColor: 'transparent',
    ...theme.typography.body2,
    fontWeight: 500,
})
const selectedTextLinkStyle: SxProps<Theme> = { color: 'primary.700', borderBottomColor: 'primary.700' }
const buttonTextLinkStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    height: 34,
    borderRadius: 1,
    mt: 3,
    px: 2,
    py: 0.5,
    '&:hover': { backgroundColor: 'grey.200' },
}
const iconTextLinkStyle: SxProps<Theme> = {
    mr: 3,
    width: 20,
    height: 20,
    color: 'grey.500',
}
const selectedIconTextLinkStyle: SxProps<Theme> = { color: 'inherit' }

// right nav items styles (icon links)
const linkIconLinkStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    p: 2,
    cursor: 'pointer',
    color: 'grey.500',
    '&:hover': { color: 'grey.700' },
}
const selectedIconLinkStyle: SxProps<Theme> = { color: 'primary.700' }
const iconIconLinkStyle: SxProps<Theme> = {
    width: 22,
    height: 22,
    color: 'inherit',
}
const selectedIconIconLinkStyle: SxProps<Theme> = { color: 'primary.700' }

const ExternalLink: FC<Readonly<{ onClick: VoidFunction } | { documentation: DocumentationType }> & { icon: FC<SvgIconProps>, title: Translation }> = ({
    icon: Icon,
    title,
    ...props
}) => {
    const translator = useTranslator()
    const linkTitle = translator.formatTranslation(title)

    if ('documentation' in props) {
        return (
            <DocumentationLink {...props} type={props.documentation} title={linkTitle} sx={linkIconLinkStyle}>
                <Icon sx={iconIconLinkStyle} />
            </DocumentationLink>
        )
    }

    return (
        <Link {...props} onClick={props.onClick} title={linkTitle} sx={linkIconLinkStyle}>
            <Icon sx={iconIconLinkStyle} />
        </Link>
    )
}

const NavigationLink: FC<Readonly<{ route: AbsoluteRouteOnlyPath }>> = ({ route }) => {
    const routing = useRouting()
    const formatter = useNavigationFormatter()
    const selected = routing.isAtRoute(route)
    const Icon = formatter.formatIcon(route)

    return (
        <NavigationPermissionedLink path={route} sx={[linkTextLinkStyle, selected && selectedTextLinkStyle]}>
            <Box sx={buttonTextLinkStyle}>
                <Icon sx={[iconTextLinkStyle, selected && selectedIconTextLinkStyle]} />
                {formatter.formatTitle(route)}
            </Box>
        </NavigationPermissionedLink>
    )
}

const NavigationIconLink: typeof NavigationLink = ({ route }) => {
    const routing = useRouting()
    const formatter = useNavigationFormatter()
    const selected = routing.isAtRoute(route)
    const Icon = formatter.formatIcon(route)

    return (
        <NavigationPermissionedLink path={route} sx={[linkIconLinkStyle, selected && selectedIconLinkStyle]}>
            <Icon sx={[iconIconLinkStyle, selected && selectedIconIconLinkStyle]} />
        </NavigationPermissionedLink>
    )
}

const appBarStyle: SxProps<Theme> = { backgroundColor: 'grey.50', boxShadow: 0, borderBottom: 1, borderColor: 'grey.300', height: TOPBAR_HEIGHT }
const toolbarStyle: SxProps<Theme> = (theme) => ({
    height: TOPBAR_HEIGHT,
    [theme.breakpoints.up('sm')]: { minHeight: TOPBAR_HEIGHT },
})
const toolbarContentStyle: SxProps<Theme> = { width: '100%', height: '100%', justifyContent: 'space-between' }
const logoWrapperStyle: SxProps<Theme> = { px: 3, mr: 2.5, ml: 1, cursor: 'pointer' }
const logoStyle: SxProps<Theme> = { height: '100%' }
const linksWrapperStyle: SxProps<Theme> = { flex: 1, px: 4 }
const emptyBoxStyle: SxProps<Theme> = { mx: 'auto' }

export const TopNavigation: FC = () => {
    const routing = useRouting()
    const logoutUsecase = useAppUsecase('logoutUsecase')

    return (
        <AppBar position="sticky" sx={appBarStyle}>
            <Toolbar disableGutters sx={toolbarStyle}>
                <Stack direction="row" sx={toolbarContentStyle} data-testid="navigation-bar">
                    <Box sx={logoWrapperStyle} onClick={() => routing.redirectHome()}>
                        <CybusMinimalLogo data-testid="cybus-top-logo" sx={logoStyle} />
                    </Box>

                    <Stack direction="row" sx={linksWrapperStyle} data-testid="navigation-list">
                        <Stack direction="row" gap={5}>
                            <NavigationLink route={AbsoluteRouteOnlyPath.SERVICES} />
                            <NavigationLink route={AbsoluteRouteOnlyPath.DATA} />
                            <NavigationLink route={AbsoluteRouteOnlyPath.USER} />
                            <NavigationLink route={AbsoluteRouteOnlyPath.SYSTEM} />
                        </Stack>

                        <Box sx={emptyBoxStyle} />

                        <SessionCountdown />

                        <Stack direction="row" gap={1.5}>
                            <ExternalLink
                                data-testid="documentation-top-link"
                                icon={DocumentationFilledIcon}
                                documentation={DocumentationType.CYBUS_DOCS}
                                title={Translation.DOCUMENTATION}
                            />
                            <NavigationIconLink route={AbsoluteRouteOnlyPath.SETTINGS} />
                            <ExternalLink
                                data-testid="logout-top-link"
                                icon={LogoutIcon}
                                onClick={() => logoutUsecase.logout(LogoutReason.MANUAL_LOGOUT)}
                                title={Translation.LOGOUT}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Toolbar>
        </AppBar>
    )
}
